/* =========================================================================================================
   NJ PRODUCT NAVIGATION MEMORY
   - On collection pages: when a product in the main product grid is clicked, remember the list of products
     the customer was looking at (in on-screen order, including filters, sorting and "load more").
   - On product pages: if the current product is in that remembered list, point the "Previous Item" /
     "Next Item" links (sections/product-navigation.liquid) to the neighbouring products in that list.
   - If nothing is remembered, or the product is not in the list, the links rendered by Liquid are left as-is.
   Storage: sessionStorage (this browser tab only). Fails silently if storage is unavailable.
   ========================================================================================================= */
(() => {
  'use strict';

  const STORAGE_KEY = 'nj_product_nav_v1';
  const MAX_AGE_MS = 1000 * 60 * 60 * 2; // 2 hours

  /* ---------- Collection page: remember the list ---------- */

  function getMainGrid(target) {
    const section = target.closest('.shopify-section--main-collection');
    return section ? section.querySelector('product-list') : null;
  }

  function collectItems(grid) {
    const items = [];
    const seen = new Set();

    grid.querySelectorAll('product-card[handle]').forEach((card) => {
      const handle = card.getAttribute('handle');
      const link = card.querySelector('a[href*="/products/"]');
      if (!handle || !link || seen.has(handle)) return;
      seen.add(handle);
      items.push({ h: handle, u: link.getAttribute('href') });
    });

    return items;
  }

  function remember(event) {
    const link = event.target.closest && event.target.closest('a[href*="/products/"]');
    if (!link) return;

    const grid = getMainGrid(link);
    if (!grid || !grid.contains(link)) return;

    const items = collectItems(grid);
    if (items.length < 2) return;

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), items: items }));
    } catch (e) {}
  }

  document.addEventListener('click', remember, { capture: true });
  document.addEventListener('auxclick', remember, { capture: true }); // middle-click / open in new tab

  /* ---------- Product page: use the remembered list ---------- */

  function readState() {
    try {
      const state = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (!state || !Array.isArray(state.items)) return null;
      if (!state.ts || Date.now() - state.ts > MAX_AGE_MS) return null;
      return state;
    } catch (e) {
      return null;
    }
  }

  function setLink(link, item) {
    if (!link) return;
    if (item && item.u) {
      link.setAttribute('href', item.u);
      link.hidden = false;
    } else {
      link.hidden = true;
    }
  }

  function applyNavigation() {
    const nav = document.querySelector('[data-nj-product-nav]');
    if (!nav) return;

    const state = readState();
    if (!state) return;

    const handle = nav.getAttribute('data-handle');
    const index = state.items.findIndex((item) => item.h === handle);
    if (index === -1) return; // not in the remembered list: keep the Liquid links

    const prev = index > 0 ? state.items[index - 1] : null;
    const next = index < state.items.length - 1 ? state.items[index + 1] : null;

    setLink(nav.querySelector('[data-nj-nav="prev"]'), prev);
    setLink(nav.querySelector('[data-nj-nav="next"]'), next);
    nav.hidden = !prev && !next;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyNavigation, { once: true });
  } else {
    applyNavigation();
  }

  // Back/forward cache: re-apply when the page is restored
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) applyNavigation();
  });
})();

/* =========================================================================================================
   NJ BREADCRUMB MEMORY (sections/nj-breadcrumb.liquid)
   - Header menu (desktop and mobile): remember which first level menu item (e.g. WOMENSWEAR / MENSWEAR) the customer
     navigated through.
   - Collection pages: when a product in the main product grid is clicked, remember the collection (handle, title and
     exact URL including filters, sorting and page) and the products shown.
   - Product pages: if the current product is in that remembered list, the breadcrumb shows that collection (with the
     exact URL) and the department the customer went through. Otherwise the Liquid breadcrumb is kept.
   Storage: sessionStorage (this browser tab only). Fails silently if storage is unavailable.
   ========================================================================================================= */
(() => {
  'use strict';

  const COLLECTION_KEY = 'nj_breadcrumb_collection_v1';
  const MENU_KEY = 'nj_breadcrumb_menu_v1';
  const MAX_AGE_MS = 1000 * 60 * 60 * 2; // 2 hours

  function save(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(Object.assign({ ts: Date.now() }, value)));
    } catch (e) {}
  }

  function read(key) {
    try {
      const state = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!state || !state.ts || Date.now() - state.ts > MAX_AGE_MS) return null;
      return state;
    } catch (e) {
      return null;
    }
  }

  function normalize(text) {
    return (text || '').replace(/\s+/g, ' ').trim().toUpperCase();
  }

  /* ---------- Header menu: remember the first level item ---------- */

  function menuTitleFor(element) {
    // Desktop header: first level item (links, dropdowns and mega menus are rendered inside it)
    const desktopItem = element.closest('.header__primary-nav-item');
    if (desktopItem) {
      const titleElement = desktopItem.querySelector('[data-title]');
      return titleElement ? titleElement.getAttribute('data-title') : null;
    }

    // Mobile menu: tab panel (WOMENSWEAR, MENSWEAR, ALL) or first level link without submenu (e.g. SALE)
    const panel = element.closest('.nj-menu-tabs__panel');
    if (panel) {
      const tab = document.getElementById(panel.getAttribute('aria-labelledby'));
      return tab ? tab.textContent : null;
    }

    if (element.classList.contains('nj-menu-tabs__link')) {
      return element.textContent;
    }

    return undefined; // not a menu link
  }

  function rememberMenu(event) {
    const target = event.target.closest && event.target.closest('a[href], summary[data-follow-link]');
    if (!target) return;

    const title = menuTitleFor(target);
    if (title === undefined) return;

    save(MENU_KEY, { title: normalize(title) });
  }

  /* ---------- Collection page: remember the collection ---------- */

  function rememberCollection(event) {
    const link = event.target.closest && event.target.closest('a[href*="/products/"]');
    if (!link) return;

    const section = link.closest('.shopify-section--main-collection');
    const grid = section ? section.querySelector('product-list') : null;
    if (!grid || !grid.contains(link)) return;

    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    if (!match) return;

    const titleMeta = document.querySelector('meta[property="og:title"]');
    const handles = [];
    grid.querySelectorAll('product-card[handle]').forEach((card) => {
      const handle = card.getAttribute('handle');
      if (handle && handles.indexOf(handle) === -1) handles.push(handle);
    });

    save(COLLECTION_KEY, {
      handle: decodeURIComponent(match[1]),
      title: titleMeta ? titleMeta.getAttribute('content') : '',
      url: window.location.pathname + window.location.search,
      items: handles
    });
  }

  function remember(event) {
    rememberMenu(event);
    rememberCollection(event);
  }

  document.addEventListener('click', remember, { capture: true });
  document.addEventListener('auxclick', remember, { capture: true }); // middle-click / open in new tab

  /* ---------- Product page: update the breadcrumb ---------- */

  function readData() {
    const script = document.querySelector('[data-nj-breadcrumb-data]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function departmentsOf(data, handle) {
    return Object.keys(data.depts).filter((key) => {
      const dept = data.depts[key];
      return dept.title && dept.handles.indexOf(handle) !== -1;
    });
  }

  function setCrumb(item, title, url) {
    if (!item) return;
    const link = item.querySelector('a');

    if (title && url) {
      link.textContent = title;
      link.setAttribute('href', url);
      item.hidden = false;
    } else {
      item.hidden = true;
    }
  }

  function applyBreadcrumb() {
    const nav = document.querySelector('[data-nj-breadcrumb]');
    if (!nav) return;

    const data = readData();
    if (!data || !data.depts) return;

    // 1. Collection: the one the customer came from (if this product was in it), else the one in the URL
    let current = null;
    const stored = read(COLLECTION_KEY);
    if (stored && Array.isArray(stored.items) && stored.items.indexOf(data.product) !== -1 && stored.title && stored.url) {
      current = stored;
    } else if (data.context) {
      current = data.context;
    }

    // 2. Department
    let deptKey = data.productDept;

    if (current) {
      const depts = departmentsOf(data, current.handle);
      const menu = read(MENU_KEY);
      const menuDept = menu
        ? Object.keys(data.depts).find((key) => data.depts[key].title && normalize(data.depts[key].title) === menu.title)
        : null;

      if (menuDept && depts.indexOf(menuDept) !== -1) {
        deptKey = menuDept; // the customer went through this department in the menu
      } else if (depts.length === 1) {
        deptKey = depts[0]; // the collection belongs to one department only
      }
    } else {
      current = data.fallback; // direct landing: category collection of the product's department
    }

    const dept = deptKey ? data.depts[deptKey] : null;
    setCrumb(nav.querySelector('[data-nj-crumb="dept"]'), dept && dept.title, dept && dept.url);

    // The department's own View All collection is not repeated
    const showCollection = current && !(dept && dept.view && dept.view === current.handle);
    setCrumb(nav.querySelector('[data-nj-crumb="collection"]'), showCollection && current.title, showCollection && current.url);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyBreadcrumb, { once: true });
  } else {
    applyBreadcrumb();
  }

  // Back/forward cache: re-apply when the page is restored
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) applyBreadcrumb();
  });
})();
